/**
 * FIRSATLAR MERKEZİ — artık KALICI FIRSAT HAVUZU'ndan okur (Orkestra Şefi Faz 1).
 * Önceden 6 kaynak tarayıcıda hesaplanıyordu: hiçbir fırsat kalıcı değildi ve
 * planlamaya köprü yoktu. Şimdi:
 *  · Gece 08:00 toplayıcısı fırsatları tek tabloda biriktirir (yaşam döngülü)
 *  · YENİ rozeti (48 saat içinde doğan) ve "kapananlar" bölümü geçmişi gösterir
 *  · Her satır tek tıkla TAKVİME dökülür (fırsat → plan köprüsü) ya da elenebilir
 * Etki puanı kaynak İÇİNDE normalize edilir; satırın aksiyonu derin ekrana götürür.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { GlPageHeader } from "@/components/ui/gl-kit";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Flame, Sparkles, Trophy, Gauge, Leaf, Newspaper, Bot, Loader2, ArrowRight, Wrench, Radar, CalendarPlus, X, RefreshCw, CheckCircle2, History } from "lucide-react";

// ── Kaynak kimlikleri: rozet rengi + hedef ekran ──
const KAYNAKLAR = {
  ai:      { ad: "AI Sorgusu",     renk: "#7a5af8", ikon: Sparkles,  href: "/ai-firsatlar" },
  serp:    { ad: "SERP Özelliği",  renk: "#2970ff", ikon: Trophy,    href: "/ai-firsatlar" },
  sxo:     { ad: "SXO",            renk: "#12b76a", ikon: Gauge,     href: "/sxo" },
  tazelik: { ad: "Tazelik",        renk: "#f79009", ikon: Leaf,      href: "/tazelik" },
  blseo:   { ad: "Backlink · SEO", renk: "#e83e3c", ikon: Newspaper, href: "/backlink-firsat" },
  blgeo:   { ad: "Backlink · GEO", renk: "#b42318", ikon: Bot,       href: "/backlink-firsat" },
  teknik:  { ad: "Teknik",         renk: "#475467", ikon: Wrench,    href: "/tech-audit" },
  dizin:   { ad: "Dizin",          renk: "#0e7090", ikon: Radar,     href: "/index-radar" },
  degisim: { ad: "İçerik Değişimi", renk: "#9333ea", ikon: History,  href: "/icerik-gunlugu" },
  bakim:   { ad: "Bakım",          renk: "#b54708", ikon: Wrench,    href: "/planning" },
} as const;
type KaynakKey = keyof typeof KAYNAKLAR;

type Firsat = {
  id: number;
  kaynak: KaynakKey;
  baslik: string;
  neden: string;
  etki: number;
  durum: "acik" | "planlandi" | "yapildi" | "kapandi" | "vazgecildi";
  aksiyon?: { tip: string; ad: string; path: string } | null;
  ilkGorulme: string;
  kapanis?: string | null;
  sonuc?: { not?: string } | null;
};
type HavuzYaniti = { satirlar: Firsat[]; kapananlar: Firsat[]; sonToplama: string | null };

const yeniMi = (f: Firsat) => Date.now() - new Date(f.ilkGorulme).getTime() < 48 * 3600_000;

export default function Firsatlar() {
  const { data: domains } = useListDomains();
  const [activeDomainId] = useActiveDomain(domains);
  const [suzgec, setSuzgec] = useState<KaynakKey | "hepsi">("hepsi");
  const { toast } = useToast();
  const qc = useQueryClient();

  const havuz = useQuery<HavuzYaniti>({
    queryKey: ["firsat-havuzu", activeDomainId],
    enabled: !!activeDomainId,
    staleTime: 60_000,
    queryFn: async () => (await fetch(`/api/domains/${activeDomainId}/firsat-havuzu`)).json(),
  });

  const topla = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/domains/${activeDomainId}/firsat-havuzu/topla`, { method: "POST" });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? "Toplama başarısız");
      return j as { yeni: number; guncellenen: number; kapanan: number };
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["firsat-havuzu", activeDomainId] });
      toast({ title: "Havuz tazelendi", description: `${r.yeni} yeni fırsat, ${r.kapanan} kapanan.` });
    },
    onError: (e) => toast({ title: "Toplama başarısız", description: String(e), variant: "destructive" }),
  });

  const planla = useMutation({
    mutationFn: async (fid: number) => {
      const r = await fetch(`/api/domains/${activeDomainId}/firsat-havuzu/${fid}/planla`, {
        method: "POST", headers: { "content-type": "application/json" }, body: "{}",
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? "Planlama başarısız");
      return j as { tarih: string };
    },
    onSuccess: (j) => {
      void qc.invalidateQueries({ queryKey: ["firsat-havuzu", activeDomainId] });
      toast({ title: "Takvime eklendi", description: `${j.tarih} gününe kart açıldı — Planlama ekranında.` });
    },
    onError: (e) => toast({ title: "Planlanamadı", description: String(e), variant: "destructive" }),
  });

  const vazgec = useMutation({
    mutationFn: async (fid: number) => {
      const r = await fetch(`/api/domains/${activeDomainId}/firsat-havuzu/${fid}/vazgec`, { method: "POST" });
      if (!r.ok) throw new Error("İşlem başarısız");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["firsat-havuzu", activeDomainId] }),
  });

  const satirlar = havuz.data?.satirlar ?? [];
  const kapananlar = havuz.data?.kapananlar ?? [];
  const gorunen = suzgec === "hepsi" ? satirlar : satirlar.filter((r) => r.kaynak === suzgec);
  const sayilar = Object.fromEntries(
    (Object.keys(KAYNAKLAR) as KaynakKey[]).map((k) => [k, satirlar.filter((r) => r.kaynak === k).length]),
  ) as Record<KaynakKey, number>;
  const hicToplanmamis = !havuz.isLoading && !havuz.data?.sonToplama;

  return (
    <div className="mx-auto max-w-5xl p-4 md:p-6">
      <GlPageHeader
        title="Fırsatlar Merkezi"
        desc="Tüm ajanların bulduğu fırsatlar kalıcı havuzda — her gece tazelenir, en değerli iş en üstte. Bir fırsatı tek tıkla takvime dök ya da ilgili uzman ekranında derinleş."
        icon={<Flame className="h-5 w-5" />}
      />

      {/* Kaynak süzgeci + tazeleme */}
      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        <button onClick={() => setSuzgec("hepsi")}
          className={`rounded-full px-3 py-1 text-[12px] font-medium transition ${suzgec === "hepsi" ? "bg-gray-800 text-white dark:bg-white dark:text-gray-900" : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-white/[0.06] dark:text-gray-300"}`}>
          Hepsi ({satirlar.length})
        </button>
        {(Object.keys(KAYNAKLAR) as KaynakKey[]).map((k) => (
          <button key={k} onClick={() => setSuzgec(k)} disabled={!sayilar[k]}
            className={`rounded-full px-3 py-1 text-[12px] font-medium transition disabled:opacity-35 ${suzgec === k ? "text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-white/[0.06] dark:text-gray-300"}`}
            style={suzgec === k ? { background: KAYNAKLAR[k].renk } : undefined}>
            {KAYNAKLAR[k].ad} ({sayilar[k]})
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          {havuz.data?.sonToplama && (
            <span className="text-[11px] text-muted-foreground">
              Son toplama: {new Date(havuz.data.sonToplama).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          <Button size="sm" variant="outline" className="gap-1.5" disabled={topla.isPending} onClick={() => topla.mutate()}>
            {topla.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Şimdi Tazele
          </Button>
        </div>
      </div>

      {havuz.isLoading ? (
        <div className="py-16 text-center text-sm text-muted-foreground"><Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />Havuz okunuyor…</div>
      ) : !satirlar.length ? (
        <Card><CardContent className="p-8 text-center">
          <Flame className="mx-auto mb-2 h-8 w-8 text-gray-300" />
          <p className="font-semibold">{hicToplanmamis ? "Havuz henüz hiç toplanmadı" : "Şimdilik bekleyen fırsat yok"}</p>
          <p className="mx-auto mt-1 max-w-md text-[12.5px] text-muted-foreground">
            {hicToplanmamis
              ? "Fırsatlar her gece 08:00'de otomatik toplanır. İlk toplamayı beklemeden şimdi başlatabilirsin — ücretsizdir, mevcut ajan verilerinden derlenir."
              : "Ajan koşuları veri ürettikçe havuz kendini doldurur: Sıralama Takibi'ne kelime ekle, AI görünürlük koşusu başlat, Google bağlantısını yap."}
          </p>
          <div className="mt-4 flex justify-center gap-2">
            {hicToplanmamis ? (
              <Button size="sm" className="gap-1.5" disabled={topla.isPending} onClick={() => topla.mutate()}>
                {topla.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                İlk Toplamayı Başlat
              </Button>
            ) : (<>
              <Link href="/rank-tracking"><Button size="sm" variant="outline">Sıralama Takibi</Button></Link>
              <Link href="/settings"><Button size="sm" variant="outline">Google'ı Bağla</Button></Link>
            </>)}
          </div>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {gorunen.map((r) => {
            const K = KAYNAKLAR[r.kaynak] ?? KAYNAKLAR.teknik;
            const Ikon = K.ikon;
            const planli = r.durum === "planlandi";
            return (
              <Card key={r.id}><CardContent className="flex items-center gap-3 p-3.5">
                <div className="flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-xl font-bold"
                  style={{ background: `${K.renk}14`, color: K.renk }}>
                  <span className="text-[15px] leading-none">{r.etki}</span>
                  <span className="mt-0.5 text-[8.5px] font-semibold uppercase opacity-70">etki</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold"
                      style={{ background: `${K.renk}14`, color: K.renk }}>
                      <Ikon className="h-3 w-3" />{K.ad}
                    </span>
                    {yeniMi(r) && !planli && (
                      <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold text-emerald-600 dark:text-emerald-400">YENİ</span>
                    )}
                    {planli && (
                      <span className="rounded-full bg-blue-500/15 px-2 py-0.5 text-[10px] font-bold text-blue-600 dark:text-blue-400">TAKVİMDE</span>
                    )}
                    <span className="truncate text-[13.5px] font-semibold">{r.baslik}</span>
                  </div>
                  <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground">{r.neden}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  {!planli && (
                    <Button size="sm" className="gap-1.5" disabled={planla.isPending} onClick={() => planla.mutate(r.id)} title="Takvime kart olarak ekle">
                      <CalendarPlus className="h-3.5 w-3.5" /> Planla
                    </Button>
                  )}
                  <Link href={r.aksiyon?.path ?? K.href}>
                    <Button size="sm" variant="outline" className="gap-1.5">
                      {r.aksiyon?.ad ?? "İncele"} <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  </Link>
                  {!planli && (
                    <button onClick={() => vazgec.mutate(r.id)} title="Bu fırsatı ele (yeniden açılmaz)"
                      className="rounded-md p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/[0.06]">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </CardContent></Card>
            );
          })}
        </div>
      )}

      {/* Kapananlar: sinyali kaybolan fırsatlar — çoğu kez "sorun çözüldü" demek */}
      {kapananlar.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-2 flex items-center gap-1.5 text-[13px] font-semibold text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" /> Son 14 günde kapananlar ({kapananlar.length})
          </h3>
          <div className="space-y-1.5">
            {kapananlar.map((r) => (
              <div key={r.id} className="flex items-center gap-2 rounded-lg border border-dashed px-3 py-2 text-[12px] text-muted-foreground dark:border-white/10">
                <span className="rounded-full px-1.5 py-0.5 text-[9.5px] font-bold" style={{ background: `${(KAYNAKLAR[r.kaynak] ?? KAYNAKLAR.teknik).renk}14`, color: (KAYNAKLAR[r.kaynak] ?? KAYNAKLAR.teknik).renk }}>
                  {(KAYNAKLAR[r.kaynak] ?? KAYNAKLAR.teknik).ad}
                </span>
                <span className="line-through decoration-emerald-500/50">{r.baslik}</span>
                {r.kapanis && <span className="ml-auto shrink-0">{new Date(r.kapanis).toLocaleDateString("tr-TR")}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="mt-3 text-[11px] text-muted-foreground">
        Havuz her gece 08:00'de tüm ajan verilerinden yeniden derlenir (ücretsiz). Etki puanı kaynak içi bir sıralama ölçüsüdür;
        "Planla" fırsatı takvim kartına çevirir — ajan koşuları günü gelince kendiliğinden çalışır.
      </p>
    </div>
  );
}
